import { createContext, useContext, type CSSProperties, type ReactNode } from "react";
import { CheckCircleIcon, InfoIcon, WarningCircleIcon, WarningIcon } from "@phosphor-icons/react";
import { Breadcrumb, Breadcrumbs as AriaBreadcrumbs, Link } from "react-aria-components";
import { cx } from "../cx.ts";

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";
/** Gebiedstint voor icoontegels (navigatie, paginakop, panelen). Nooit voor status. */
export type Hue = "teal" | "sun" | "blue" | "rose" | "gold" | "green" | "slate";

/** Icoontegel in de tint van een gebied; `solid` voor de actieve of belangrijkste plek. */
export function Mark({ icon, hue, solid, className, size }: { icon: ReactNode; hue: Hue; solid?: boolean; className?: string; size?: string }) {
  return (
    <span className={cx("ui-mark", solid && "ui-mark--solid", className)} data-hue={hue} style={size ? ({ ["--mark-size" as string]: size } as CSSProperties) : undefined} aria-hidden>
      {icon}
    </span>
  );
}

/** Het gebied van de huidige pagina (gezet door de app-shell); PageHeader toont het als tegel naast de titel. */
const PageMarkContext = createContext<{ icon: ReactNode; hue: Hue } | null>(null);
export const PageMarkProvider = PageMarkContext.Provider;

export function Badge({ tone = "neutral", icon, children, outline, className }: { tone?: Tone; icon?: ReactNode; children: ReactNode; outline?: boolean; className?: string }) {
  return (
    <span className={cx("ui-badge", tone !== "neutral" && `ui-badge--${tone}`, outline && "ui-badge--outline", className)}>
      {icon}
      {children}
    </span>
  );
}

const TONE_ICON: Partial<Record<Tone, ReactNode>> = {
  success: <CheckCircleIcon aria-hidden weight="fill" />,
  warning: <WarningIcon aria-hidden weight="fill" />,
  danger: <WarningCircleIcon aria-hidden weight="fill" />,
  info: <InfoIcon aria-hidden weight="fill" />,
};

/** Status: kleur nooit alleen; altijd met icoon en tekst. */
export function StatusBadge({ tone, children, icon }: { tone: Tone; children: ReactNode; icon?: ReactNode | null }) {
  return (
    <Badge tone={tone} icon={icon === undefined ? TONE_ICON[tone] : icon}>
      {children}
    </Badge>
  );
}

export function Panel({ title, actions, children, footer, flush, className, headingLevel = 2, id, icon, hue }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; footer?: ReactNode; flush?: boolean; className?: string; headingLevel?: 2 | 3; id?: string; icon?: ReactNode; hue?: Hue }) {
  const H = headingLevel === 2 ? "h2" : "h3";
  return (
    <section className={cx("ui-panel", className)} aria-labelledby={title && id ? `${id}-title` : undefined} id={id}>
      {title || actions ? (
        <div className="ui-panel__head">
          {title ? (
            <H id={id ? `${id}-title` : undefined}>
              {icon && hue ? <Mark icon={icon} hue={hue} /> : null}
              {title}
            </H>
          ) : (
            <span />
          )}
          {actions ? <div className="cluster">{actions}</div> : null}
        </div>
      ) : null}
      <div className={cx("ui-panel__body", flush && "ui-panel__body--flush")}>{children}</div>
      {footer ? <div className="ui-panel__foot">{footer}</div> : null}
    </section>
  );
}

export function Notice({ tone = "neutral", title, children, actions, icon, role }: { tone?: Tone; title?: ReactNode; children?: ReactNode; actions?: ReactNode; icon?: ReactNode; role?: "status" | "alert" }) {
  return (
    <div className={cx("ui-notice", tone !== "neutral" && `ui-notice--${tone}`)} role={role}>
      {icon ?? TONE_ICON[tone] ?? <InfoIcon aria-hidden />}
      <div className="stack" style={{ ["--gap" as string]: "2px" } as CSSProperties}>
        {title ? <div className="ui-notice__title">{title}</div> : null}
        {children ? <div className="ui-notice__body">{children}</div> : null}
      </div>
      {actions ? <div className="ui-notice__actions">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({ icon, title, children, actions, headingLevel = 3 }: { icon: ReactNode; title: ReactNode; children?: ReactNode; actions?: ReactNode; headingLevel?: 1 | 2 | 3 }) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div className="ui-empty">
      <div className="ui-empty__icon" aria-hidden>
        {icon}
      </div>
      <Heading>{title}</Heading>
      {children ? <p>{children}</p> : null}
      {actions ? <div className="cluster">{actions}</div> : null}
    </div>
  );
}

export function Skeleton({ width, height, className }: { width?: string; height?: string; className?: string }) {
  return <span className={cx("ui-skeleton", className)} style={{ width, height }} aria-hidden />;
}

/** Laadtoestand in de vorm van de uiteindelijke lijst. */
export function SkeletonRows({ rows = 6, label }: { rows?: number; label: string }) {
  return (
    <div className="stack" role="status" aria-label={label} style={{ ["--gap" as string]: "var(--space-3)", padding: "var(--space-4)" } as CSSProperties}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="cluster" style={{ ["--gap" as string]: "var(--space-3)", flexWrap: "nowrap" } as CSSProperties}>
          <Skeleton width="3.75rem" height="2.75rem" />
          <div className="stack" style={{ ["--gap" as string]: "6px", flex: 1 } as CSSProperties}>
            <Skeleton width={`${40 + ((i * 17) % 35)}%`} />
            <Skeleton width={`${20 + ((i * 11) % 20)}%`} height="0.7rem" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="ui-kbd">{children}</kbd>;
}

export function Avatar({ name, size }: { name: string; size?: "lg" }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
  return (
    <span className={cx("ui-avatar", size === "lg" && "ui-avatar--lg")} aria-hidden>
      {initials}
    </span>
  );
}

/** Verbruiksbalk: de waarde staat altijd als tekst in beeld; de balk zelf is decoratief. */
export function Meter({ label, value, max = 100, valueLabel, tone }: { label: string; value: number; max?: number; valueLabel?: string; tone?: "warning" | "danger" }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className={cx("ui-meter", tone && `ui-meter--${tone}`)}>
      <div className="ui-meter__labels">
        <span>{label}</span>
        <span>{valueLabel ?? `${Math.round(pct)}%`}</span>
      </div>
      <div className="ui-meter__track" aria-hidden>
        <div className="ui-meter__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function DescriptionList({ items, className }: { items: { term: ReactNode; detail: ReactNode }[]; className?: string }) {
  return (
    <dl className={cx("ui-dl", className)}>
      {items.map((item, i) => (
        <div key={i} style={{ display: "contents" }}>
          <dt>{item.term}</dt>
          <dd>{item.detail}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Breadcrumbs({ items, label }: { items: { label: ReactNode; href?: string }[]; label: string }) {
  return (
    <AriaBreadcrumbs className="ui-breadcrumbs" aria-label={label}>
      {items.map((item, i) => (
        <Breadcrumb key={i} className="ui-breadcrumb">
          {item.href ? <Link href={item.href}>{item.label}</Link> : <span>{item.label}</span>}
        </Breadcrumb>
      ))}
    </AriaBreadcrumbs>
  );
}

export function PageHeader({ title, lead, actions, breadcrumbs, meta, mark }: { title: ReactNode; lead?: ReactNode; actions?: ReactNode; breadcrumbs?: ReactNode; meta?: ReactNode; mark?: { icon: ReactNode; hue: Hue } | false }) {
  const area = useContext(PageMarkContext);
  const shown = mark === false ? null : (mark ?? area);
  return (
    <header className="ui-page-header">
      {breadcrumbs}
      <div className="ui-page-header__row">
        <div className="ui-page-header__title">
          {shown ? <Mark icon={shown.icon} hue={shown.hue} solid /> : null}
          <div className="ui-page-header__heading">
            <h1>{title}</h1>
            {meta}
          </div>
        </div>
        {actions ? <div className="ui-page-header__actions">{actions}</div> : null}
      </div>
      {lead ? <p className="ui-page-header__lead">{lead}</p> : null}
    </header>
  );
}
